import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Copy, Check, BookOpen, ExternalLink, FlaskConical } from 'lucide-react';

// Lightweight code-block with copy button. We build snippets as plain strings
// so there are no syntax-highlighting dependencies to ship.
function CodeBlock({ language, code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative border border-zinc-200 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-200 bg-zinc-50">
        <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{language}</span>
        <button onClick={copy} className="flex items-center gap-1 text-xs text-zinc-600 hover:text-black"
          data-testid={`${testid}-copy`}>
          {copied ? <><Check className="w-3.5 h-3.5"/> copied</> : <><Copy className="w-3.5 h-3.5"/> copy</>}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3 text-xs font-mono leading-relaxed text-zinc-800 bg-white">{code}</pre>
    </div>
  );
}

function Section({ id, eyebrow, title, children }) {
  return (
    <section id={id} className="border border-zinc-200 rounded-sm p-6 scroll-mt-20">
      {eyebrow && <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{eyebrow}</p>}
      <h2 className="font-display text-2xl font-semibold tracking-tight mt-1">{title}</h2>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-zinc-700">{children}</div>
    </section>
  );
}

const sandboxRows = [
  { filename: 'approved.jpg', status: 'approved', score: '0.95', notes: 'High-confidence pass.' },
  { filename: 'rejected.jpg', status: 'rejected', score: '0.10', notes: 'Below acceptance threshold.' },
  { filename: 'review.jpg',   status: 'needs_review', score: '0.55', notes: 'Borderline match.' },
  { filename: 'anything.jpg', status: 'approved', score: '0.95', notes: 'Default profile when no keyword matches.' },
];

export default function Docs() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    api.get('/organization').then(({ data }) => setOrg(data.organization)).catch(() => {});
  }, []);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const apiKey = org?.api_key || 'idg_<your_api_key>';
  const secret = org?.webhook_secret || 'whsec_<your_secret>';

  const snippets = useMemo(() => ({
    curlCreate: `curl -X POST ${baseUrl}/api/v1/verification-sessions \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "applicant_email": "lea@acme.eu",
    "applicant_reference": "user_42",
    "claimed_first_name": "Lea",
    "claimed_last_name": "Dubois",
    "send_email": true
  }'`,
    nodeCreate: `import fetch from 'node-fetch';

const r = await fetch('${baseUrl}/api/v1/verification-sessions', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.IDEMGO_API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    applicant_email: 'lea@acme.eu',
    applicant_reference: 'user_42',
    claimed_first_name: 'Lea',
    claimed_last_name: 'Dubois',
    send_email: true,
  }),
});
const data = await r.json();
console.log(data.public_url); // share this with the applicant`,
    pythonCreate: `import os, requests

resp = requests.post(
    "${baseUrl}/api/v1/verification-sessions",
    headers={
        "Authorization": f"Bearer {os.environ['IDEMGO_API_KEY']}",
        "Content-Type": "application/json",
    },
    json={
        "applicant_email": "lea@acme.eu",
        "applicant_reference": "user_42",
        "claimed_first_name": "Lea",
        "claimed_last_name": "Dubois",
        "send_email": True,
    },
    timeout=15,
)
resp.raise_for_status()
print(resp.json()["public_url"])`,
    curlGet: `curl ${baseUrl}/api/v1/verification-sessions/<id> \\
  -H "Authorization: Bearer ${apiKey}"`,
    nodeVerify: `import crypto from 'node:crypto';
import express from 'express';

const app = express();
app.use(express.raw({ type: 'application/json' })); // raw body required

app.post('/idemgo/webhook', (req, res) => {
  const header = req.headers['x-idemgo-signature'] || '';
  const parts = Object.fromEntries(header.split(',').map(p => p.split('=')));
  const expected = crypto
    .createHmac('sha256', process.env.IDEMGO_WEBHOOK_SECRET)
    .update(\`\${parts.t}.\${req.body.toString('utf8')}\`)
    .digest('hex');

  if (!crypto.timingSafeEqual(Buffer.from(expected, 'hex'), Buffer.from(parts.v1, 'hex'))) {
    return res.status(400).send('bad signature');
  }
  // Optional: reject events older than 5 minutes.
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) {
    return res.status(400).send('stale event');
  }
  const event = JSON.parse(req.body.toString('utf8'));
  const resource = event.session || event.data;
  console.log(event.type, resource.status);
  res.sendStatus(200);
});`,
    pythonVerify: `import hmac, hashlib, json, os, time
from fastapi import FastAPI, Request, HTTPException

app = FastAPI()
SECRET = os.environ["IDEMGO_WEBHOOK_SECRET"].encode()

@app.post("/idemgo/webhook")
async def webhook(request: Request):
    raw = await request.body()
    header = request.headers.get("x-idemgo-signature", "")
    parts = dict(p.split("=", 1) for p in header.split(","))
    expected = hmac.new(SECRET, f"{parts['t']}.{raw.decode()}".encode(),
                        hashlib.sha256).hexdigest()
    if not hmac.compare_digest(expected, parts.get("v1", "")):
        raise HTTPException(400, "bad signature")
    if abs(time.time() - int(parts["t"])) > 300:
        raise HTTPException(400, "stale event")
    event = json.loads(raw)
    resource = event.get("session") or event.get("data")
    print(event["type"], resource["status"])
    return {"ok": True}`,
    sandboxCurl: `# Sandbox: filename drives the outcome.
curl -X POST ${baseUrl}/api/public/sessions/<token>/submit \\
  -F "id_document_front=@./approved.jpg" \\
  -F "id_document_back=@./approved.jpg" \\
  -F "selfie=@./any-selfie.jpg"
# -> { "session": { "status": "approved", "face_match_score": 0.95, "sandbox": true } }`,
  }), [baseUrl, apiKey]);

  return (
    <div data-testid="docs-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Developers</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <BookOpen className="w-6 h-6" strokeWidth={1.5}/> Developer documentation
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Pre-filled with your API key and webhook secret. Copy any snippet and paste it into your stack.
          </p>
        </div>
      </div>

      {/* Quick nav */}
      <nav className="flex flex-wrap gap-2 text-xs font-mono">
        {[
          ['#auth', 'Auth'],
          ['#create', 'Create session'],
          ['#get', 'Read session'],
          ['#webhooks', 'Webhooks'],
          ['#verify', 'Verify signature'],
          ['#sandbox', 'Sandbox'],
        ].map(([href, label]) => (
          <a key={href} href={href} className="border border-zinc-200 px-3 py-1.5 rounded-sm hover:border-zinc-400">
            {label}
          </a>
        ))}
      </nav>

      <Section id="auth" eyebrow="01" title="Authentication">
        <p>
          All requests to <code className="font-mono text-xs bg-zinc-100 px-1">/api/v1/*</code> require a Bearer token
          using your organization's API key. Treat it like a password.
        </p>
        <div className="flex items-center gap-2 border border-zinc-200 rounded-sm bg-zinc-50">
          <code data-testid="docs-api-key" className="flex-1 px-3 py-2 text-xs font-mono truncate">{apiKey}</code>
          <span className="px-3 py-2 border-l border-zinc-200 text-xs text-zinc-500">{user?.role === 'admin' ? 'rotate in Integration' : 'admin can rotate'}</span>
        </div>
      </Section>

      <Section id="create" eyebrow="02" title="Create a KYC verification session">
        <p>
          Returns a <code className="font-mono text-xs">public_url</code> you can send to the applicant. Set
          <code className="font-mono text-xs"> send_email: true</code> to let idemGO deliver it for you (requires Resend configured).
        </p>
        <p>
          Optional <code className="font-mono text-xs">claimed_*</code> fields prefill the applicant's claimed identity and are compared
          against document evidence during verification. Applicants normally enter their date of birth in the hosted flow. If you provide
          <code className="font-mono text-xs"> claimed_dob</code> through the API, it must use ISO format
          <code className="font-mono text-xs"> YYYY-MM-DD</code>. Full identity claim comparison currently requires first name, last name, and DOB.
        </p>
        <CodeBlock language="curl"   testid="snippet-create-curl"   code={snippets.curlCreate}/>
        <CodeBlock language="node.js" testid="snippet-create-node"   code={snippets.nodeCreate}/>
        <CodeBlock language="python" testid="snippet-create-python" code={snippets.pythonCreate}/>
      </Section>

      <Section id="get" eyebrow="03" title="Read a KYC verification result">
        <CodeBlock language="curl" testid="snippet-get-curl" code={snippets.curlGet}/>
        <p className="text-xs text-zinc-500">
          Response shape: <code className="font-mono text-xs">{`{ session: { id, status, extracted_name, extracted_dob, extracted_document_number, face_match_score, reason, sandbox, ... } }`}</code>
        </p>
      </Section>

      <Section id="webhooks" eyebrow="04" title="Webhooks">
        <p>
          idemGO sends webhook events to your configured endpoint whenever important verification or document-signing events occur.
          The body is JSON and the <code className="font-mono text-xs">X-Idemgo-Signature</code> header lets you verify that the request originated from idemGO.
        </p>
        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
                <th className="px-4 py-2 font-medium">Group</th>
                <th className="px-4 py-2 font-medium">Events</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-zinc-100">
                <td className="px-4 py-3 text-zinc-500 align-top">Verification events</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {['verification.completed', 'verification.needs_review', 'verification.rejected', 'verification.expired', 'verification.revoked'].map((event) => (
                      <span key={event} className="border border-zinc-200 px-2 py-1 rounded-sm font-mono text-xs text-zinc-700">{event}</span>
                    ))}
                  </div>
                </td>
              </tr>
              <tr className="border-b border-zinc-100">
                <td className="px-4 py-3 text-zinc-500 align-top">Document events</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {['document.signed', 'document.revoked', 'document.expired'].map((event) => (
                      <span key={event} className="border border-zinc-200 px-2 py-1 rounded-sm font-mono text-xs text-zinc-700">{event}</span>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-zinc-500 align-top">Planned</td>
                <td className="px-4 py-3">
                  <span className="border border-zinc-200 px-2 py-1 rounded-sm font-mono text-xs text-zinc-700">document.rejected</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <CodeBlock
            language="verification payload"
            testid="snippet-webhook-verification-payload"
            code={`{
  "type": "verification.completed",
  "session": {
    "id": "...",
    "status": "approved"
  },
  "data": {
    "id": "...",
    "status": "approved",
    "sandbox": false
  }
}`}
          />
          <CodeBlock
            language="document payload"
            testid="snippet-webhook-document-payload"
            code={`{
  "type": "document.signed",
  "data": {
    "id": "...",
    "status": "signed",
    "sandbox": false
  }
}`}
          />
        </div>
        <p className="text-xs text-zinc-500">
          <code className="font-mono text-xs">verification.completed</code> remains backward compatible and includes the full
          <code className="font-mono text-xs"> session</code> object. Other webhook events use a lightweight
          <code className="font-mono text-xs"> data</code> object containing identifiers, status information and timestamps.
        </p>
        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-zinc-100"><td className="px-4 py-2 text-zinc-500 w-48">Header</td><td className="px-4 py-2 font-mono text-xs">X-Idemgo-Event: verification.completed</td></tr>
              <tr className="border-b border-zinc-100"><td className="px-4 py-2 text-zinc-500">Header</td><td className="px-4 py-2 font-mono text-xs">X-Idemgo-Signature: t=&lt;unix_ts&gt;,v1=&lt;sha256_hex&gt;</td></tr>
              <tr className="border-b border-zinc-100"><td className="px-4 py-2 text-zinc-500">Verification payload</td><td className="px-4 py-2 font-mono text-xs">{`{ "type": "verification.completed", "session": { ... }, "data": { "id": "...", "status": "approved", ... } }`}</td></tr>
              <tr className="border-b border-zinc-100"><td className="px-4 py-2 text-zinc-500">Document payload</td><td className="px-4 py-2 font-mono text-xs">{`{ "type": "document.signed", "data": { "id": "...", "status": "signed", ... } }`}</td></tr>
              <tr className="border-b border-zinc-100"><td className="px-4 py-2 text-zinc-500">Retry</td><td className="px-4 py-2 font-mono text-xs">4 attempts · 0/1/5/25 s backoff · 2xx stops, 4xx (≠ 408/429) stops</td></tr>
              <tr><td className="px-4 py-2 text-zinc-500">Your secret</td><td className="px-4 py-2 font-mono text-xs">{secret}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="border border-zinc-200 bg-white px-3 py-2 rounded-sm text-xs text-zinc-600">
          Webhook payloads intentionally exclude document images, raw OCR output, biometric data and other sensitive evidence.
          Use the API to retrieve additional information when required.
        </div>
      </Section>

      <Section id="verify" eyebrow="05" title="Verify a webhook signature">
        <p>
          Compute <code className="font-mono text-xs">HMAC-SHA256("{`<t>.<raw_body>`}", secret)</code> and compare it to the
          <code className="font-mono text-xs"> v1</code> field. Reject events older than 5 minutes to avoid replay.
        </p>
        <CodeBlock language="node.js" testid="snippet-verify-node"   code={snippets.nodeVerify}/>
        <CodeBlock language="python"  testid="snippet-verify-python" code={snippets.pythonVerify}/>
      </Section>

      <Section id="sandbox" eyebrow="06" title={<span className="inline-flex items-center gap-2"><FlaskConical className="w-5 h-5" strokeWidth={1.5}/>Sandbox cheatsheet</span>}>
        <p>
          Toggle <strong>Sandbox mode</strong> in Settings. Future sessions will skip real KYC verification and return a deterministic outcome
          based on the uploaded ID document filename — perfect for CI / integration tests.
        </p>
        {org?.sandbox_mode ? (
          <div className="border border-amber-400 bg-amber-50 px-3 py-2 rounded-sm text-xs text-amber-900" data-testid="docs-sandbox-on">
            Sandbox is currently <strong>ON</strong> for <code className="font-mono">{org.name}</code>.
          </div>
        ) : (
          <div className="border border-zinc-200 px-3 py-2 rounded-sm text-xs text-zinc-600" data-testid="docs-sandbox-off">
            Sandbox is currently OFF — sessions go through the full KYC verification pipeline.
          </div>
        )}
        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="sandbox-cheatsheet">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
                <th className="px-4 py-2 font-medium">Filename keyword</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Score</th>
                <th className="px-4 py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {sandboxRows.map((r) => (
                <tr key={r.filename} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{r.filename}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.status}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.score}</td>
                  <td className="px-4 py-2 text-xs text-zinc-600">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CodeBlock language="curl" testid="snippet-sandbox-curl" code={snippets.sandboxCurl}/>
        <p className="text-xs text-zinc-500 inline-flex items-center gap-1">
          Webhooks still fire in sandbox so you can test your receiver end-to-end. <ExternalLink className="w-3 h-3"/>
        </p>
      </Section>
    </div>
  );
}
